import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    BannerType,
    BannerView,
    colors,
    NavigationArrowRightRoundedIcon
} from "@dotnet/telematics-design-system";

import Dialog from "components/DesignComponents/Dialog";
import QueryDropdown from "components/QueryDropdown";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import AlertContext from "context/alert/alertContext";
import ParamsContext from "context/params/paramsContext";
import { useApi } from "shared/hooks/useApi";
import { svgFontSize } from "shared/styles/styleFunctions";
import {
    COLUMN_RESOURCES,
    DROPDOWN_SIZES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    ID_TYPE,
    TABLE_NAMES
} from "shared/types/enums";

import { DialogProps, GROUP_ACTIONS } from "../../../types";
import DialogActions from "../../components/DialogActions";
import { DropdownValue } from "../../types";

const MoveGroup = ({ isOpen, close }: DialogProps) => {
    const { gray700 } = colors;

    const { removeAlertForBGAction, setBackgroundActionId } =
        useContext(AlertContext);

    const { setReloadItems } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { handleResponse, postData } = useApi();

    const [isLoading, setIsLoading] = useState(false);
    const [group, setGroup] = useState<DropdownValue | null>(null);
    const [company, setCompany] = useState<DropdownValue | null>(null);

    const isReady = group && company;

    const handleCloseDialog = () => close(GROUP_ACTIONS.MoveDeviceGroup);

    const handleSubmit = async () => {
        removeAlertForBGAction();

        try {
            setIsLoading(true);

            const payload = {
                target_company_id: company?.id
            };

            const { data } = await postData(
                `${ENDPOINTS.Groups}/${group?.id}${ENDPOINT_PATH_SEGMENTS.Move}`,
                payload
            );

            setBackgroundActionId(
                data,
                TABLE_NAMES.DeviceGroups,
                () => {
                    handleCloseDialog();
                    setReloadItems(TABLE_NAMES.DeviceGroups);
                },
                () => setIsLoading(false)
            );
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            title={t("Dialog##move device group")}
            description={t("Dialog##move device group description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={isLoading ? undefined : handleCloseDialog}
            extendToMaxWidth
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        removeAlertForBGAction();
                        setIsLoading(false);
                        setGroup(null);
                        setCompany(null);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    loaderText={t("Button##moving")}
                    isReadyToConfirm={isReady}
                    confirmationButtonText={t("Button##move")}
                />
            }
        >
            <>
                <BannerView
                    bannerType={BannerType.Info}
                    title={t("Dialog##move device group banner")}
                    css={css({
                        margin: "8px 0 16px 0"
                    })}
                />

                <div
                    css={css({
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "16px"
                    })}
                >
                    <QueryDropdown
                        resource={ENDPOINTS.Groups}
                        idType={ID_TYPE.Id}
                        size={DROPDOWN_SIZES.Medium}
                        fullWidth
                        change={(_, val) => setGroup(val)}
                        labelLeft={t("Dialog##select group")}
                        textFieldParams={{
                            placeholder: t("Dialog##select")
                        }}
                        readOnly={isLoading}
                        description={{
                            resource: COLUMN_RESOURCES.Id,
                            name: t("General##id")
                        }}
                        isRootRequired
                    />

                    <NavigationArrowRightRoundedIcon
                        css={css({
                            color: gray700,
                            margin: "24px 8px 0 8px",
                            flexShrink: "0",
                            ...svgFontSize(16)
                        })}
                    />

                    <CompanyDropdown
                        labelLeft={t("Dialog##select company")}
                        readOnly={isLoading}
                        change={(_, val) => setCompany(val)}
                        isRootRequired
                    />
                </div>
            </>
        </Dialog>
    );
};

export default MoveGroup;
